import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import {
  PageHeader, AdminCard, DataTable, StatusPill, SearchInput, FilterChips, RowMenu,
  Modal, Drawer, ConfirmDialog, Field, Btn, PageLoader, datetimeFromIso, Spinner,
  Input, Textarea,
} from "@/admin/components";
import { MonthCalendar, todayISO, isoPlusDays } from "@/components/jk/MonthCalendar";
import { formatINR, formatDate, formatTime } from "@/lib/format";

const FILTERS = ["All", "Cancelled"];
const METHODS = [
  { value: "upi", label: "UPI" },
  { value: "bank", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
];

const EMPTY_MR_FORM = { refundType: "full", amount: "", method: "upi", reference: "", note: "" };

export default function Bookings() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState(params.get("q") || "");
  const [filter, setFilter] = useState("All");
  const [detail, setDetail] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [reschedule, setReschedule] = useState(null);
  const [rsSlots, setRsSlots] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingRefundsCount, setPendingRefundsCount] = useState(0);

  // Manual refund modal state
  const [mrTarget, setMrTarget] = useState(null);
  const [mrForm, setMrForm] = useState(EMPTY_MR_FORM);
  const [mrStep, setMrStep] = useState("form"); // "form" | "success"
  const [mrError, setMrError] = useState("");
  const [mrResult, setMrResult] = useState(null);
  const [mrBusy, setMrBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/bookings", { params: { search, filter } });
    setRows(data.bookings);
    setPendingRefundsCount(data.pending_refunds_count || 0);
  }, [search, filter]);

  useEffect(() => { load(); }, [load]);

  const loadSlots = useCallback(async (id, date) => {
    if (!id || !date) { setRsSlots(null); return; }
    setRsSlots({ loading: true });
    try {
      const { data } = await adminApi.get(`/admin/bookings/${id}/slots`, { params: { date } });
      setRsSlots(data);
    } catch {
      setRsSlots({ groups: { morning: [], afternoon: [], evening: [] } });
    }
  }, []);

  const openReschedule = (r) => {
    setReschedule({ id: r.id, date: r.booking_date, time: "" });
    loadSlots(r.id, r.booking_date);
  };

  const pickDate = (date) => {
    setReschedule((r) => ({ ...r, date, time: "" }));
    loadSlots(reschedule.id, date);
  };

  const openDetail = async (b) => {
    setDetail({ loading: true });
    const { data } = await adminApi.get(`/admin/bookings/${b.id}`);
    setDetail(data);
  };

  const act = async (id, path, msg) => {
    try {
      await adminApi.post(`/admin/bookings/${id}/${path}`);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/bookings/${cancelTarget.id}/cancel`);
      toast.success("Booking cancelled");
      setCancelTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const doRefund = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/bookings/${refundTarget.id}/refund`);
      toast.success(data.already_refunded ? "Already refunded" : (data.refunded ? "Refund issued" : "Refund flagged (manual follow-up needed)"));
      setRefundTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally { setBusy(false); }
  };

  const doReschedule = async () => {
    if (!reschedule.date || !reschedule.time) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/bookings/${reschedule.id}/reschedule`, {
        booking_date: reschedule.date, booking_time: reschedule.time,
      });
      toast.success("Booking rescheduled");
      setReschedule(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reschedule failed");
    } finally { setBusy(false); }
  };

  const openManualRefund = (r) => {
    setMrTarget(r);
    setMrForm({ ...EMPTY_MR_FORM, amount: String(r.fee_inr) });
    setMrStep("form");
    setMrError("");
    setMrResult(null);
  };

  const closeMr = () => {
    setMrTarget(null);
    if (mrStep === "success") load();
  };

  const doManualRefund = async () => {
    if (!mrTarget) return;
    const amount = parseFloat(mrForm.amount);
    if (mrForm.refundType === "partial" && (isNaN(amount) || amount <= 0)) {
      setMrError("Enter a valid refund amount.");
      return;
    }
    if (mrForm.refundType === "partial" && amount > mrTarget.fee_inr) {
      setMrError(`Amount cannot exceed ₹${mrTarget.fee_inr}.`);
      return;
    }
    setMrError("");
    setMrBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/bookings/${mrTarget.id}/manual-refund`, {
        refund_type: mrForm.refundType,
        refund_amount_inr: mrForm.refundType === "full" ? mrTarget.fee_inr : amount,
        refund_method: mrForm.method,
        refund_reference: mrForm.reference,
        note: mrForm.note,
      });
      setMrResult(data);
      setMrStep("success");
    } catch (e) {
      setMrError(e?.response?.data?.detail || "Could not record refund.");
    } finally { setMrBusy(false); }
  };

  const columns = [
    {
      key: "astrologer_name", label: "Astrologer",
      render: (r) => (
        <div>
          <div className="font-medium text-[#111]">{r.astrologer_name || "—"}</div>
          <span className="mt-1 inline-block rounded-md bg-[#f2f0ec] px-1.5 py-0.5 font-mono text-[11px] text-[#666]">#{r.booking_reference}</span>
        </div>
      ),
    },
    {
      key: "client_name", label: "Client",
      render: (r) => (
        <div>
          <div className="font-medium text-[#111]">{r.client_name || "—"}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            <span className="rounded-full border border-[#e9e6e1] px-2 py-0.5 text-[11px] text-[#555]">{r.service_name}</span>
            {r.consultation_mode && <span className="rounded-full border border-[#e9e6e1] px-2 py-0.5 text-[11px] text-[#555]">{r.consultation_mode}</span>}
          </div>
        </div>
      ),
    },
    { key: "booking_date", label: "Date & Time", render: (r) => <span>{formatDate(r.booking_date)} · {formatTime(r.booking_time)}</span> },
    {
      key: "fee_inr", label: "Fee",
      render: (r) => (
        <div>
          <div className="font-medium text-[#111]">{formatINR(r.fee_inr)}</div>
          {r.status === "cancelled" && (
            <span className="mt-1 inline-block rounded-full bg-[#fdecea] px-2 py-0.5 text-[11px] font-semibold text-[#c0392b]">Cancelled</span>
          )}
        </div>
      ),
    },
    {
      key: "payment_status", label: "Payment",
      render: (r) => {
        if (r.refund_status === "manual_refund") return <StatusPill status="manual_refund" tone="amber" label="Refunded" />;
        if (r.refund_status === "refunded") return <StatusPill status="refunded" />;
        if (r.payment_status === "paid") return <StatusPill status="paid" />;
        return <span className="text-[#bbb]">—</span>;
      },
    },
    {
      key: "actions", label: "", sortable: false,
      render: (r) => {
        const canRefund = r.payment_status === "paid" && !["refunded", "manual_refund"].includes(r.refund_status);
        return (
          <RowMenu
            testid={`booking-menu-${r.id}`}
            items={[
              { label: "View Full Detail", testid: `booking-view-${r.id}`, onClick: () => openDetail(r) },
              { label: "Reschedule", testid: `booking-reschedule-${r.id}`, onClick: () => openReschedule(r) },
              { label: "Cancel Booking", testid: `booking-cancel-${r.id}`, danger: true, onClick: () => setCancelTarget(r) },
              ...(canRefund ? [
                { label: "Refund Payment", testid: `booking-refund-${r.id}`, danger: true, onClick: () => setRefundTarget(r) },
                { label: "Mark as Manually Refunded", testid: `booking-manual-refund-${r.id}`, onClick: () => openManualRefund(r) },
              ] : []),
              { label: "Resend Confirmation Email", onClick: () => act(r.id, "resend-email", "Confirmation email resent") },
              ...(r.payment_status === "paid" && r.razorpay_payment_id
                ? [{
                    label: "View in Razorpay",
                    testid: `booking-razorpay-${r.id}`,
                    onClick: () => window.open(`https://dashboard.razorpay.com/app/payments/${r.razorpay_payment_id}`, "_blank", "noopener,noreferrer"),
                  }]
                : []),
            ]}
          />
        );
      },
    },
  ];

  return (
    <div data-testid="admin-bookings-page">
      <PageHeader title="Bookings" subtitle={rows ? `${rows.length} bookings` : ""} />

      {pendingRefundsCount > 0 && (
        <AdminCard className="mb-5 flex items-center gap-3 border-l-4 border-[#c0392b] p-4" data-testid="pending-refunds-card">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#999]">Pending Refunds</p>
            <p className="mt-1 text-[28px] font-bold leading-none text-[#c0392b]">{pendingRefundsCount}</p>
          </div>
          <p className="ml-2 text-[13px] text-[#555]">
            {pendingRefundsCount === 1 ? "booking has" : "bookings have"} been cancelled and flagged for manual refund.
          </p>
        </AdminCard>
      )}

      <div className="mb-5 flex flex-col gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by reference, astrologer, or client name..." testid="booking-search" />
        <FilterChips options={FILTERS} value={filter} onChange={setFilter} testidPrefix="booking-filter" />
      </div>

      <AdminCard className="overflow-hidden">
        {rows ? <DataTable columns={columns} rows={rows} testid="bookings-table" empty="No bookings match your filters." /> : <PageLoader />}
      </AdminCard>

      {/* Detail drawer */}
      <Drawer open={!!detail} onClose={() => setDetail(null)} title="Booking Detail" testid="booking-detail-drawer">
        {detail?.loading ? (
          <PageLoader />
        ) : detail ? (
          <div className="space-y-5 text-[14px]">
            <div className="flex items-center justify-between">
              <span className="rounded-md bg-[#f2f0ec] px-2.5 py-1 font-mono text-[13px]">#{detail.booking.booking_reference}</span>
              <StatusPill status={detail.booking.status} />
            </div>
            <Grid>
              <Cell label="Astrologer" value={detail.astrologer?.full_name} />
              <Cell label="Service" value={detail.service_name} />
              <Cell label="Date" value={formatDate(detail.booking.booking_date)} />
              <Cell label="Time" value={formatTime(detail.booking.booking_time)} />
              <Cell label="Mode" value={detail.booking.consultation_mode} />
              <Cell label="Fee" value={formatINR(detail.booking.fee_inr)} />
              <Cell label="Payment" value={detail.booking.payment_status} />
            </Grid>
            {detail.client && (
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#999]">Client</p>
                <Grid>
                  <Cell label="Name" value={detail.client.full_name} />
                  <Cell label="Mobile" value={`${detail.client.mobile_country_code || ""} ${detail.client.mobile_number || ""}`.trim()} breakAll />
                  <Cell label="Email" value={detail.client.email} breakAll />
                  <Cell label="DOB" value={detail.client.date_of_birth || "—"} />
                  <Cell label="TOB" value={detail.client.time_of_birth || "—"} />
                  <Cell label="POB" value={detail.client.place_of_birth || "—"} />
                </Grid>
              </div>
            )}
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#999]">Notification Log</p>
              {detail.notifications.length === 0 ? (
                <p className="text-[#999]">No notifications recorded.</p>
              ) : (
                <div className="space-y-1.5">
                  {detail.notifications.map((n) => (
                    <div key={n.id} className="flex items-center justify-between rounded-[8px] bg-[#f9f8f5] px-3 py-2">
                      <span className="text-[13px] text-[#555]">{n.channel.toUpperCase()} · {n.trigger_type} · {n.recipient_type}</span>
                      <span className="flex items-center gap-2 text-[12px] text-[#999]">
                        {datetimeFromIso(n.created_at)} <StatusPill status={n.status} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Drawer>

      {/* Cancel confirm */}
      <ConfirmDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={doCancel}
        busy={busy}
        title="Cancel this booking?"
        message={cancelTarget ? `Booking #${cancelTarget.booking_reference} will be cancelled and the client notified. Paid bookings are flagged for manual refund.` : ""}
        confirmLabel="Cancel Booking"
      />

      {/* Razorpay refund confirm */}
      <ConfirmDialog
        open={!!refundTarget}
        onClose={() => setRefundTarget(null)}
        onConfirm={doRefund}
        busy={busy}
        danger
        title="Refund this payment?"
        message={refundTarget ? `The paid amount for booking #${refundTarget.booking_reference} (${formatINR(refundTarget.fee_inr)}) will be refunded via the original payment provider and the booking cancelled. The client will be notified.` : ""}
        confirmLabel="Issue Refund"
      />

      {/* Reschedule modal */}
      <Modal open={!!reschedule} onClose={() => setReschedule(null)} title="Reschedule Booking" testid="admin-reschedule-modal">
        {reschedule && (
          <div className="space-y-4">
            <Field label="New date">
              <MonthCalendar
                value={reschedule.date}
                onSelect={pickDate}
                minDate={todayISO()}
                maxDate={isoPlusDays(60)}
                testid="admin-reschedule-calendar"
              />
            </Field>
            <Field label="New time">
              {!rsSlots || rsSlots.loading ? (
                <div className="flex items-center gap-2 py-3 text-[13px] text-[#999]"><Spinner className="h-4 w-4" /> Loading slots…</div>
              ) : rsSlots.blocked ? (
                <p className="py-2 text-[13px] text-[#c0392b]">This date is blocked in the astrologer&apos;s calendar.</p>
              ) : rsSlots.out_of_window ? (
                <p className="py-2 text-[13px] text-[#c0392b]">This date is outside the booking window.</p>
              ) : (() => {
                const all = [...rsSlots.groups.morning, ...rsSlots.groups.afternoon, ...rsSlots.groups.evening];
                if (all.length === 0) return <p className="py-2 text-[13px] text-[#999]">No slots configured for this day.</p>;
                return (
                  <div className="flex flex-wrap gap-2" data-testid="admin-reschedule-slots">
                    {all.map((s) => (
                      <button
                        key={s.time}
                        type="button"
                        disabled={!s.available}
                        data-testid={`admin-reschedule-slot-${s.time}`}
                        onClick={() => setReschedule((r) => ({ ...r, time: s.time }))}
                        className={`rounded-[10px] border px-3 py-2 text-[13px] font-medium transition-colors ${
                          reschedule.time === s.time
                            ? "border-[#111] bg-[#111] text-white"
                            : s.available
                              ? "border-[#e5e5e5] text-[#111] hover:border-[#111]"
                              : "cursor-not-allowed border-[#f0f0f0] text-[#cfccc6] line-through"
                        }`}
                      >
                        {formatTime(s.time)}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <Btn variant="light" onClick={() => setReschedule(null)}>Cancel</Btn>
              <Btn testid="admin-reschedule-confirm" busy={busy} disabled={!reschedule.time} onClick={doReschedule}>Confirm</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* Manual refund modal */}
      <Modal open={!!mrTarget} onClose={closeMr} title="Record Manual Refund" testid="admin-manual-refund-modal">
        {mrTarget && mrStep === "form" && (
          <div className="space-y-4">
            <div className="rounded-[10px] bg-[#f9f8f5] px-4 py-3 text-[13px] text-[#555]">
              <span className="font-mono text-[#111]">#{mrTarget.booking_reference}</span>
              {mrTarget.astrologer_name && <span className="ml-2 text-[#999]">· {mrTarget.astrologer_name}</span>}
              <span className="ml-2 font-semibold text-[#111]">{formatINR(mrTarget.fee_inr)}</span>
            </div>

            <Field label="Refund type">
              <div className="flex gap-2">
                {["full", "partial"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`mr-type-${t}`}
                    onClick={() => {
                      setMrForm((f) => ({ ...f, refundType: t, amount: t === "full" ? String(mrTarget.fee_inr) : "" }));
                      setMrError("");
                    }}
                    className={`flex-1 rounded-[10px] border px-4 py-2.5 text-[13px] font-semibold capitalize transition-colors ${
                      mrForm.refundType === t ? "border-[#111] bg-[#111] text-white" : "border-[#e5e5e5] text-[#555]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Refund amount (₹)">
              <Input
                type="number"
                min="1"
                max={mrTarget.fee_inr}
                data-testid="mr-amount"
                value={mrForm.refundType === "full" ? mrTarget.fee_inr : mrForm.amount}
                readOnly={mrForm.refundType === "full"}
                onChange={(e) => { setMrForm((f) => ({ ...f, amount: e.target.value })); setMrError(""); }}
                className={mrForm.refundType === "full" ? "bg-[#f2f0ec] text-[#999] cursor-not-allowed" : ""}
              />
            </Field>

            <Field label="Refund method">
              <select
                data-testid="mr-method"
                value={mrForm.method}
                onChange={(e) => setMrForm((f) => ({ ...f, method: e.target.value }))}
                className="h-11 w-full rounded-[10px] border border-[#e5e5e5] bg-white px-3 text-[14px] text-[#111] outline-none focus:border-[#111]"
              >
                {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>

            <Field label="Reference / UTR (optional)">
              <Input
                data-testid="mr-reference"
                placeholder="Transaction ID, UTR, or receipt number"
                value={mrForm.reference}
                onChange={(e) => setMrForm((f) => ({ ...f, reference: e.target.value }))}
              />
            </Field>

            <Field label="Note (optional)">
              <Textarea
                data-testid="mr-note"
                rows={2}
                placeholder="Internal note…"
                value={mrForm.note}
                onChange={(e) => setMrForm((f) => ({ ...f, note: e.target.value }))}
              />
            </Field>

            {mrError && (
              <p className="text-[13px] text-[#c0392b]" data-testid="mr-error">{mrError}</p>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <Btn variant="light" onClick={closeMr}>Cancel</Btn>
              <Btn testid="mr-confirm" busy={mrBusy} onClick={doManualRefund}>Confirm Refund</Btn>
            </div>
          </div>
        )}

        {mrTarget && mrStep === "success" && mrResult && (
          <div className="space-y-4">
            <div className="rounded-[10px] bg-[#e8f3ec] px-4 py-3">
              <p className="text-[14px] font-semibold text-[#1a7a45]">Refund recorded successfully</p>
            </div>
            <div className="rounded-[10px] bg-[#f9f8f5] px-4 py-4 space-y-2 text-[13px]">
              <SummaryRow label="Booking" value={`#${mrTarget.booking_reference}`} />
              <SummaryRow label="Amount" value={formatINR(mrResult.refund_amount_inr)} />
              <SummaryRow label="Method" value={METHODS.find((m) => m.value === mrResult.refund_method)?.label || mrResult.refund_method} />
              {mrForm.reference && <SummaryRow label="Reference" value={mrForm.reference} />}
            </div>
            <div className="flex justify-end pt-1">
              <Btn testid="mr-done" onClick={closeMr}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

const Grid = ({ children }) => <div className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</div>;
const Cell = ({ label, value, breakAll }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.06em] text-[#999]">{label}</div>
    <div className={`text-[14px] text-[#222] ${breakAll ? "break-all" : ""}`}>{value || "—"}</div>
  </div>
);
const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-[#777]">{label}</span>
    <span className="font-medium text-[#111]">{value}</span>
  </div>
);
